import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FormControlLabel,
  FormGroup,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';

import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import DescriptionIcon from '@mui/icons-material/Description';

import toast from 'react-hot-toast';
import DetailSectionsLoading from '../../components/Cards/DetailSectionsLoading/DetailSectionsLoading';
import WidgetCard from '../../components/Cards/WidgetCard/WidgetCard';
import DetailPageLayout from '../../components/layouts/DetailPageLayout/DetailPageLayout';
import ReadonlyInfoItem from '../../components/Cards/DataInfoCard/ReadonlyInfoItem';
import { formatInZonedTime } from '../../utils/dateTime';
import useAuth from '../../hooks/useAuth';
import { getCountryByCountryId, updateCountry } from '../../services/api/CountryApi';
import { isEmpty, isEqual } from 'lodash';

export const ObjectTypeOptions = [{ text: 'Finding' }, { text: 'WorkOrder' }, { text: 'WorkOrderForm' }];

const CountryEdit = () => {
  const { countryCode } = useParams();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [dataObject, setDataObject] = useState({});
  const { timezone, dateFormat } = useAuth();

  const breadcrumbsLinks = useMemo(
    () => [
      { to: '/', text: t('Breadcrumb_Home') },
      { to: '/countries', text: t('Header_Country') },
      { text: t('Header_EditCountry') },
    ],
    [t]
  );

  const addEditOnLoad = useCallback(async (abortController) => {
    if (!countryCode) {
      return;
    }
    try {
      setIsLoading(true);
      const response = await getCountryByCountryId({ countryCode }, abortController);
      if (response.length && Array.isArray(response)) {
        setDataObject(response?.[0] || {});
      }
    } catch (error) {
      console.log('Error', error);
    }
    setIsLoading(false);
  }, [countryCode]);

  useEffect(() => {
    const abortController = new AbortController();
    addEditOnLoad(abortController);
    return () => {
      abortController.abort();
    };
  }, [addEditOnLoad]);

  const onSaveHandler = async (values) => {
    const { CountryCode, CountryName, CountryCode2, Active, CountryNumericCode, Description, CustomFields } = values || {};

    try {
      const CustomFieldsData = CustomFields?.filter((item) => item.FieldName || item.FieldLabel || item.FieldValue)
        .map((item) => ({
          FieldName: item.FieldName,
          FieldLabel: item.FieldLabel,
          FieldValue: item.FieldValue,
        }));

      const payload = {
        CountryCode,
        CountryName,
        CountryCode2,
        CountryNumericCode,
        Description,
        Active,
        ...(CustomFieldsData?.length && { CustomFields: CustomFieldsData }),
      };
      const response = await updateCountry([payload]);
      if (response.IsSuccess) {
        toast.success(t('Toast_UpdatedSuccessfully'));
        await addEditOnLoad();
      } else {
        toast.error(t('Error_Message_SomethingWentWrong'));
      }
    } catch (err) {
      toast.error(t('Error_Message_SomethingWentWrong'));
      console.log(err);
    }
  };

  const initialValues = {
    CountryCode: dataObject?.CountryCode || '',
    CountryName: dataObject?.CountryName || '',
    CountryCode2: dataObject?.CountryCode2 || '',
    Active: dataObject?.Active ?? false,
    CountryNumericCode: dataObject?.CountryNumericCode ?? '',
    Description: dataObject?.Description ?? '',
    CustomFields: isEmpty(dataObject.CustomFields) ? [] : dataObject.CustomFields,
  };

  const validationSchema = Yup.object().shape({
    CountryCode: Yup.string()
      .max(3)
      .required(t('Error_Required', { fieldName: 'Country Code' })),
    CountryName: Yup.string()
      .max(50)
      .required(t('Error_Required', { fieldName: 'Country Name' })),
  });

  let ObjectCountryBody = null;
  if (isLoading) {
    ObjectCountryBody = <DetailSectionsLoading widgetCount={2} itemCountArr={[6, 4]} />;
  } else if (!dataObject) {
    ObjectCountryBody = <Typography variant="h6" color="error">{t('Label_CountryDataDoesNotExist')}</Typography>;
  } else {
    ObjectCountryBody = (
      <>
        <Stack spacing={3}>
          <Box>
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onSaveHandler}
              validateOnBlur
              validateOnChange
              validateOnMount
            >
              {({
                handleSubmit, isValid, isSubmitting, values, handleChange, handleBlur, touched, errors, setFieldValue
              }) => {
                const isSaveBtnEnabled = isEqual(initialValues, values);
                return (
                  <form onSubmit={handleSubmit}>
                    <WidgetCard
                      expandable
                      elevation={6}
                      icon={<DescriptionIcon fontSize="small" color="action" />}
                      title={t('Label_CountryInformation')}
                      body={
                        <>
                          <Grid
                            container
                            spacing={2}
                            sx={{
                              p: 1,
                              mb: 2
                            }}
                          >
                            <Grid item xs={12} lg={4} md={4} sm={6}>
                              <TextField
                                label={`${t('Label_CountryCode')} *`}
                                fullWidth
                                variant="outlined"
                                size="small"
                                name="CountryCode"
                                value={values?.CountryCode}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={Boolean(touched.CountryCode && errors.CountryCode)}
                                helperText={touched.CountryCode && errors.CountryCode}
                                inputProps={{
                                  maxLength: 3,
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} lg={4} md={4} sm={6}>
                              <TextField
                                label={`${t('Label_CountryCode2')}`}
                                fullWidth
                                variant="outlined"
                                size="small"
                                name="CountryCode2"
                                value={values?.CountryCode2}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            </Grid>
                            <Grid item xs={12} lg={4} md={4} sm={6}>
                              <TextField
                                label={`${t('Label_CountryCodeNumber')}`}
                                fullWidth
                                variant="outlined"
                                size="small"
                                name="CountryNumericCode"
                                value={values?.CountryNumericCode}
                                onChange={(e) => {
                                  const numericValue = e.target.value.replace(/\D/g, '');
                                  handleChange({ target: { name: 'CountryNumericCode', value: numericValue } });
                                }}
                                onBlur={handleBlur}
                                inputProps={{
                                  maxLength: 3,
                                  pattern: '[0-9]*',
                                  inputMode: 'numeric',
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} lg={12} md={4} sm={6}>
                              <TextField
                                label={`${t('Label_CountryName')} *`}
                                fullWidth
                                variant="outlined"
                                size="small"
                                name="CountryName"
                                value={values?.CountryName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={Boolean(touched.CountryName && errors.CountryName)}
                                helperText={touched.CountryName && errors.CountryName}
                                inputProps={{
                                  maxLength: 50,
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} lg={12} md={4} sm={6}>
                              <TextField
                                label={`${t('Label_Description')}`}
                                fullWidth
                                variant="outlined"
                                size="small"
                                name="Description"
                                value={values?.Description}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            </Grid>
                          </Grid>

                          <Box sx={{
                            px: 3,
                            mt: 2
                          }}
                          >

                            <FormGroup>
                              <FormControlLabel
                                control={<Switch size="small" />}
                                label={t('Label_Active')}
                                name="Active"
                                checked={!!values.Active}
                                onBlur={handleBlur}
                                onChange={(evt, newValue) => setFieldValue('Active', newValue)}
                              />
                            </FormGroup>
                          </Box>
                          <Box
                            sx={{
                              px: 3,
                              mt: 2
                            }}
                          >
                            <Stack spacing={2} direction="row">
                              <ReadonlyInfoItem label={t('Label_CreatedBy')} value={dataObject?.CreatedByName} />
                              <ReadonlyInfoItem label={t('Label_CreatedOn')} value={formatInZonedTime(dataObject?.CreatedOn, dateFormat, timezone)} />
                              <ReadonlyInfoItem label={t('Label_UpdatedBy')} value={dataObject?.LastUpdatedByName} />
                              <ReadonlyInfoItem label={t('Label_UpdatedOn')} value={formatInZonedTime(dataObject?.LastUpdatedOn, dateFormat, timezone)} />
                            </Stack>
                          </Box>
                        </>
                    }
                    />
                    <Box sx={{
                      mt: 2
                    }}
                    >
                      <LoadingButton
                        color="primary"
                        size="small"
                        type="submit"
                        variant="contained"
                        startIcon={<SaveOutlinedIcon />}
                        loading={isSubmitting}
                        loadingPosition="start"
                        disabled={Boolean(!isValid) || isSaveBtnEnabled}
                      >
                        {t('Btn_Save')}
                      </LoadingButton>
                    </Box>
                  </form>
                );
              }}
            </Formik>
          </Box>
        </Stack>
      </>
    );
  }

  return (
    <DetailPageLayout
      title={t('Header_EditCountry')}
      headerText={t('Header_EditCountry')}
      breadcrumbsLinks={breadcrumbsLinks}
      BodyComponent={<>{ObjectCountryBody}</>}
    />
  );
};

export default CountryEdit;
